import { DatePipe, DecimalPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
    importProvidersFrom,
    inject,
    Injector,
    LOCALE_ID,
    NgModule,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbar } from '@angular/material/toolbar';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import { ROUTES } from '@data-terminal/base/app/app.routes';
import { DataTerminalIconsModule } from '@data-terminal/base/app/data-terminal-icons.module';
import { AppConfigService } from '@data-terminal/data-access/app-config/app-config.service';
import { createAuthConfig } from '@data-terminal/data-access/create-auth-config/create-auth-config.function';
import { createMaskConfig } from '@data-terminal/data-access/create-mask-config/create-mask-config.function';
import { GlobalErrorHandler } from '@data-terminal/data-access/error';
import { GLOBAL_RX_STATE, GlobalState } from '@data-terminal/data-access/global-state/global-rx.state';
import { OrganizationUrlInterceptor } from '@data-terminal/data-access/http-interceptors';
import { initGA4Service } from '@data-terminal/data-access/init-ga4-service/init-ga4-service.function';
import {
    APP_RUNTIME,
    AppRuntime,
    ORGANIZATION_REQUEST_PREFIX,
    WINDOW,
} from '@data-terminal/data-access/injection-tokens';
import { onThemeInit } from '@data-terminal/data-access/on-theme-init/on-theme-init.function';
import { provideAppConfigProvider } from '@data-terminal/data-access/provide-app-config-provider/provide-app-config-provider.function';
import { provideContactService } from '@data-terminal/data-access/provide-contact-service/provide-contact-service.function';
import { provideNavigationService } from '@data-terminal/data-access/provide-navigation-service/provide-navigation-service.function';
import { provideNotificationsService } from '@data-terminal/data-access/provide-notifications-service/provide-notifications-service.function';
import { provideTaskPilotProvider } from '@data-terminal/data-access/provide-task-pilot-provider/provide-task-pilot-provider.function';
import { provideUserProvider } from '@data-terminal/data-access/provide-user-provider/provide-user-provider.function';
import { environment } from '@data-terminal/environment';
import { FeatureTransitionModule } from '@data-terminal/feature-transition/feature-transition.module';
import { LoadingIndicatorComponentModule } from '@data-terminal/ui-presentational/loading-indicator';
import { SignInConnectIndicatorComponentModule } from '@data-terminal/ui-presentational/sign-in-connect-indicator';
import { ShutdownBlockerComponent } from '@data-terminal/ui-smart/components/shutdown-blocker/shutdown-blocker.component';
import { ToastNotificationComponent } from '@data-terminal/ui-smart/components/toast-notification/toast-notification.component';
import { BackgroundScannerComponent } from '@data-terminal/ui-smart/dialogs/background-scanner/background-scanner.component';
import { FooterVisibleDirective } from '@data-terminal/utils/directives';
import { AcceptHeaderInterceptor, LoadingSpinnerInterceptor } from '@data-terminal/utils/http-interceptors';
import { LoadingStateService, SnackBarModule } from '@data-terminal/utils/services';
import { ConfigService, ControlCenterNotificationModule } from '@heidelberg/control-center-frontend-integration';
import { CC_APP_AUTH_CONFIG, CCAuthService, provideCCAuth } from '@heidelberg/control-center-frontend-integration/auth';
import {
    HDMUI_APP_CONFIG_PROVIDER,
    HDMUI_CONTACT_SERVICE,
    HDMUI_NAVIGATION_SERVICE,
    HDMUI_NOTIFICATIONS_SERVICE,
    HDMUI_TASK_PILOT_PROVIDER,
    HDMUI_USER_PROVIDER,
    HdmuiScreenBlockerModule,
    IconRegistryService,
    provideHdmui,
    provideHdmuiNotificationCenter,
    ThemeService,
} from '@heidelberg/hdmui-angular';
import {
    ChatbotWidgetDrawerLayoutComponent,
    provideChatBot,
    provideChatBotConfig,
} from '@heidelberg/hplus-ngx-chatbot-integration';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { AppComponent } from './components/app/app.component';
import { FooterComponentModule } from './components/footer/footer.component';
import { LegalLineComponent } from './components/legal-line/legal-line.component';
import { NavigationComponentModule } from './components/navigation/navigation.component';
import { ToolbarComponentModule } from './components/toolbar/toolbar.component';
import { createTranslateLoader, getLocale, getLocaleForDatePicker, initLangSettings } from './locale.support';
import { Ga4Service } from './services/ga4.service';

@NgModule({
    declarations: [AppComponent, FooterVisibleDirective],
    imports: [
        BrowserModule,
        RouterModule.forRoot(ROUTES, {
            paramsInheritanceStrategy: 'always',
            useHash: true,
            onSameUrlNavigation: 'reload',
        }),
        ToolbarComponentModule,
        MatSidenavModule,
        FooterComponentModule,
        MatDialogModule,
        NavigationComponentModule,
        LoadingIndicatorComponentModule,
        SignInConnectIndicatorComponentModule,
        SnackBarModule,
        FeatureTransitionModule,
        MatTabsModule,
        LegalLineComponent,
        HdmuiScreenBlockerModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
        }),
        BackgroundScannerComponent,
        ShutdownBlockerComponent,
        ToastNotificationComponent,
        MatNativeDateModule,
        MatIconButton,
        MatToolbar,
        MatIcon,
        MatSidenavModule,
        ChatbotWidgetDrawerLayoutComponent,
        DataTerminalIconsModule,
    ],
    providers: [
        provideZoneChangeDetection({ eventCoalescing: false }),
        provideAnimations(),
        DecimalPipe,
        DatePipe,
        provideHdmui(),
        provideHttpClient(withInterceptorsFromDi()),
        importProvidersFrom(ControlCenterNotificationModule),
        ...provideHdmuiNotificationCenter(),
        provideCCAuth(),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AcceptHeaderInterceptor,
            multi: true,
        },
        {
            provide: CC_APP_AUTH_CONFIG,
            useFactory: createAuthConfig,
            deps: [AppConfigService, ConfigService],
        },
        provideAppInitializer(() => {
            const iconRegistry = inject(IconRegistryService);
            iconRegistry.registerAppIcon('assets/icons/HDM-Data-Capture-Nearlywhite.svg');
        }),
        provideAppInitializer(() => {
            const initializerFn = onThemeInit(inject(ThemeService), inject(WINDOW));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initLangSettings(inject(TranslateService), inject(CCAuthService));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initGA4Service(inject(Ga4Service));
            return initializerFn();
        }),
        {
            provide: LOCALE_ID,
            useFactory: getLocale,
            deps: [TranslateService],
        },
        {
            provide: MAT_DATE_LOCALE,
            useFactory: getLocaleForDatePicker,
            deps: [TranslateService],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: OrganizationUrlInterceptor,
            multi: true,
            deps: [CCAuthService, ORGANIZATION_REQUEST_PREFIX],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoadingSpinnerInterceptor,
            multi: true,
            deps: [LoadingStateService],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: GlobalErrorHandler,
            deps: [Injector, CCAuthService, TranslateService, Router],
            multi: true,
        },
        { provide: GLOBAL_RX_STATE, useFactory: () => new RxState<GlobalState>() },
        provideEnvironmentNgxMask(createMaskConfig),
        RxState,
        { provide: APP_RUNTIME, useValue: AppRuntime.REGULAR },
        provideChatBot(
            provideChatBotConfig({
                BASE_URL: environment.CHATBOT_URL,
                LOAD_AS_MODULE: false,
                LAZY_LOAD_WEB_COMPONENT: true,
            })
        ),
        {
            provide: HDMUI_CONTACT_SERVICE,
            useFactory: provideContactService,
        },
        {
            provide: HDMUI_USER_PROVIDER,
            useFactory: provideUserProvider,
        },
        {
            provide: HDMUI_APP_CONFIG_PROVIDER,
            useFactory: provideAppConfigProvider,
        },
        {
            provide: HDMUI_NAVIGATION_SERVICE,
            useFactory: provideNavigationService,
        },
        {
            provide: HDMUI_NOTIFICATIONS_SERVICE,
            useFactory: provideNotificationsService,
        },
        {
            provide: HDMUI_TASK_PILOT_PROVIDER,
            useFactory: provideTaskPilotProvider,
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
